/**
 * Shrink photos in the browser before uploading them.
 *
 * A phone photo is commonly 3-5MB. Resized to 1600px and re-encoded as JPEG it
 * lands around 200-400KB with no visible difference at the sizes this site
 * displays. The point is less about storage cost than about uploads finishing:
 * people photograph gear at trailheads and boat ramps on one bar of signal,
 * where a 4MB upload times out and a 300KB one does not.
 */

/**
 * iPhones and Macs hand over .HEIC files. Chrome, Firefox and Android cannot
 * display them at all, so one uploaded unconverted is an invisible photo on a
 * listing — the upload appears to succeed and the picture never shows. Safari
 * can decode HEIC natively; everywhere else needs a WebAssembly decoder, which
 * is large, so it is loaded only when a HEIC actually turns up.
 */
const HEIC_EXTENSION = /\.(heic|heif)$/i;
const HEIC_MIME = /^image\/(heic|heif)/i;

export function isHeicFile(file) {
  if (!file) return false;
  // macOS sometimes reports an empty type, so the filename is the fallback.
  return HEIC_MIME.test(file.type || '') || HEIC_EXTENSION.test(file.name || '');
}

export const MAX_IMAGE_DIMENSION = 1600;
export const IMAGE_QUALITY = 0.8;
/** Below this, re-encoding tends to cost more than it saves. */
export const SKIP_COMPRESSION_UNDER_BYTES = 300 * 1024;

function canUseCanvas() {
  return (
    typeof document !== 'undefined' &&
    typeof HTMLCanvasElement !== 'undefined' &&
    typeof URL !== 'undefined'
  );
}

/**
 * Decode a file into something drawable, letting the browser apply the EXIF
 * orientation so photos taken sideways do not upload sideways.
 */
async function decodeImage(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (error) {
      // Safari has historically rejected the options argument; fall through.
      try {
        return await createImageBitmap(file);
      } catch (retryError) {
        // fall through to the <img> path
      }
    }
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read that image.'));
    };
    img.src = url;
  });
}

/** Rename to .jpg so the stored file and its type agree. */
function toJpegFile(blob, originalName) {
  const base = (originalName || 'photo').replace(/\.[^.]+$/, '');
  return new File([blob], `${base}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
}

/**
 * Convert a HEIC to JPEG. Tries the browser's own decoder first (Safari does it
 * natively and instantly) and only pulls in the WebAssembly decoder when that
 * fails, which it does on Chrome, Firefox and Android.
 */
async function convertHeicToJpeg(file, quality) {
  if (canUseCanvas()) {
    try {
      const source = await decodeImage(file);
      const width = source.width || source.naturalWidth;
      const height = source.height || source.naturalHeight;
      if (width && height) {
        const canvas = document.createElement('canvas');
        const size = scaledSize(width, height, MAX_IMAGE_DIMENSION);
        canvas.width = size.width;
        canvas.height = size.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(source, 0, 0, size.width, size.height);
          if (typeof source.close === 'function') source.close();
          const blob = await canvasToBlob(canvas, quality);
          if (blob) return toJpegFile(blob, file.name);
        }
      }
    } catch (error) {
      // Expected on Chrome/Firefox, which cannot decode HEIC. Fall through.
    }
  }

  // ~2MB of WebAssembly, so it is fetched only for an actual HEIC. Takes a few
  // seconds on a full-resolution iPhone photo; callers should show progress.
  const { heicTo } = await import('heic-to');
  const blob = await heicTo({ blob: file, type: 'image/jpeg', quality });
  if (!blob) throw new Error('Could not read that photo.');
  return toJpegFile(blob, file.name);
}

function scaledSize(width, height, maxDimension) {
  if (width <= maxDimension && height <= maxDimension) {
    return { width, height };
  }
  const ratio = width > height ? maxDimension / width : maxDimension / height;
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
  });
}

/**
 * Returns a compressed File, or the original if compressing is unnecessary or
 * fails. Never throws: a photo that will not compress is still worth uploading.
 */
export async function compressImage(file, options = {}) {
  const {
    maxDimension = MAX_IMAGE_DIMENSION,
    quality = IMAGE_QUALITY,
    skipUnderBytes = SKIP_COMPRESSION_UNDER_BYTES,
  } = options;

  if (!file) return file;

  // HEIC must be converted whatever its size — leaving it alone means a photo
  // that silently never appears. This throws on failure rather than returning
  // the original, because uploading an unviewable file is the worse outcome.
  if (isHeicFile(file)) {
    const asJpeg = await convertHeicToJpeg(file, quality);
    // The decoder returns the photo at full resolution, so send it back through
    // for the usual resize; it is a plain JPEG now, so this cannot recurse.
    return compressImage(asJpeg, options);
  }

  if (!file.type || !file.type.startsWith('image/')) return file;
  // GIFs would lose animation, and SVGs are already small and not raster.
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') return file;
  if (file.size <= skipUnderBytes) return file;
  if (!canUseCanvas()) return file;

  try {
    const source = await decodeImage(file);
    const sourceWidth = source.width || source.naturalWidth;
    const sourceHeight = source.height || source.naturalHeight;
    if (!sourceWidth || !sourceHeight) return file;

    const { width, height } = scaledSize(sourceWidth, sourceHeight, maxDimension);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(source, 0, 0, width, height);
    if (typeof source.close === 'function') source.close();

    const blob = await canvasToBlob(canvas, quality);
    if (!blob || blob.size >= file.size) return file;

    const name = file.name ? file.name.replace(/\.[^.]+$/, '') : 'photo';
    return new File([blob], `${name}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch (error) {
    console.warn('Image compression failed, uploading original:', error);
    return file;
  }
}

/** Compress several files, keeping originals for any that fail. */
export async function compressImages(files, options = {}) {
  const list = Array.from(files || []);
  return Promise.all(list.map((file) => compressImage(file, options)));
}
